// Cookie-consent store (localStorage-backed, framework-agnostic). The single source of
// truth for which non-essential cookie categories the visitor has agreed to. Non-essential
// categories default to OFF until the visitor opts in via the CookieBanner.

export interface ConsentCategories {
  necessary: true; // always on (strictly necessary)
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = 'easydent.cookieConsent';
const CONSENT_VERSION = 1; // bump to re-ask everyone when the cookie policy materially changes

interface StoredConsent {
  v: number;
  categories: ConsentCategories;
  at: string;
}

type Listener = (categories: ConsentCategories | null) => void;
let listeners: Listener[] = [];

/** Returns the saved consent, or null if the visitor hasn't chosen yet (or the version changed). */
export function getConsent(): ConsentCategories | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.v !== CONSENT_VERSION) return null;
    return parsed.categories;
  } catch {
    return null;
  }
}

/** Persists the visitor's choice and notifies subscribers (e.g. the analytics loader). */
export function setConsent(choice: { analytics: boolean; marketing: boolean }): void {
  const categories: ConsentCategories = { necessary: true, analytics: choice.analytics, marketing: choice.marketing };
  const stored: StoredConsent = { v: CONSENT_VERSION, categories, at: new Date().toISOString() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* storage may be unavailable; consent simply won't persist */
  }
  listeners.forEach((l) => l(categories));
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function onConsentChange(fn: Listener): () => void {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((l) => l !== fn);
  };
}
